// lib/mockData.js
// -----------------------------------------------------------------------------
// In-memory fake data layer used while the app runs WITHOUT Supabase.
// The mock client in lib/supabase.js reads/writes these arrays so the whole
// site is clickable end-to-end with realistic sample data.
//
// Shapes here are the raw DATABASE (snake_case) shapes. Context layers
// (e.g. TournamentContext.formatTournamentData) map them to camelCase, so match
// the DB column names + embedded relation names used in the .select() queries.
// -----------------------------------------------------------------------------

const now = Date.now();
const daysFromNow = (d) => new Date(now + d * 86400000).toISOString();
const isoDate = (d) => daysFromNow(d).split('T')[0];

// ---------------------------------------------------------------------------
// The fake signed-in auth user (what supabase.auth.getSession() returns)
// ---------------------------------------------------------------------------
export const currentAuthUser = {
  id: 'user-me',
  email: 'you@rematch.pro',
  user_metadata: {
    full_name: 'You',
    name: 'You',
    avatar_url: '/avatars/p1.webp',
    provider_id: '000000000000000000'
  },
  app_metadata: { provider: 'discord' }
};

export const currentSession = {
  access_token: 'mock-access-token',
  refresh_token: 'mock-refresh-token',
  expires_at: Math.floor(now / 1000) + 3600,
  token_type: 'bearer',
  user: currentAuthUser
};

// ---------------------------------------------------------------------------
// users
// ---------------------------------------------------------------------------
const mkUser = (id, username, display, opts = {}) => ({
  id,
  username,
  display_name: display,
  avatar_url: opts.avatar || null,
  original_discord_avatar: opts.avatar || null,
  use_discord_avatar: false,
  discord_id: opts.discord || `1000000000000000${id.slice(-2)}`,
  role: opts.role || 'user',
  bio: opts.bio || '',
  platform: opts.platform || 'steam',
  gamer_tag: opts.tag || username,
  position: opts.position || 'Midfielder',
  jersey_number: opts.jersey ?? null,
  country_code: opts.country || 'PT',
  tournaments_played: opts.tp ?? 5,
  tournaments_won: opts.tw ?? 1,
  total_wins: opts.wins ?? 20,
  total_losses: opts.losses ?? 12,
  elo_rating: opts.elo ?? 1050,
  created_at: daysFromNow(-120)
});

export const users = [
  mkUser('user-me', 'YouPlayer', 'You', { avatar: '/avatars/p1.webp', role: 'admin', country: 'PT', tp: 14, tw: 4, wins: 52, losses: 18, elo: 1320, position: 'Forward', jersey: 10 }),
  mkUser('user-1', 'Nova', 'Nova Strike', { avatar: '/avatars/p2.webp', country: 'ES', elo: 1210, position: 'Forward', jersey: 7 }),
  mkUser('user-2', 'Kairo', 'Kairo', { avatar: '/avatars/p3.webp', country: 'FR', elo: 1180, position: 'Goalkeeper', jersey: 1 }),
  mkUser('user-3', 'Zephyr', 'Zephyr', { avatar: '/avatars/p4.webp', country: 'DE', elo: 1145, position: 'Defender', jersey: 4 }),
  mkUser('user-4', 'Milo', 'Milo', { avatar: '/avatars/p5.webp', country: 'IT', elo: 1090, position: 'Midfielder', jersey: 8 }),
  mkUser('user-5', 'Riven', 'Riven', { avatar: '/avatars/p6.webp', country: 'BR', elo: 1275, position: 'Forward', jersey: 9 }),
  mkUser('user-6', 'Sol', 'Sol', { avatar: '/avatars/p7.webp', country: 'GB', elo: 1030, position: 'Defender', jersey: 5 }),
  mkUser('user-7', 'Ash', 'Ash', { avatar: '/avatars/p8.webp', country: 'TR', elo: 1005, position: 'Midfielder', jersey: 6 }),
  mkUser('user-8', 'Vale', 'Vale', { avatar: '/avatars/p9.webp', country: 'PL', elo: 1160, position: 'Goalkeeper', jersey: 12 })
];

const userById = (id) => users.find((u) => u.id === id);
const publicUser = (id) => {
  const u = userById(id);
  if (!u) return null;
  return { id: u.id, username: u.username, display_name: u.display_name, avatar_url: u.avatar_url, gamer_tag: u.gamer_tag };
};

// ---------------------------------------------------------------------------
// teams + team_members
// ---------------------------------------------------------------------------
const mkTeam = (id, name, ownerId, memberIds, opts = {}) => ({
  id,
  name,
  logo: opts.logo || null,
  description: opts.description || `${name} — competitive Rematch squad.`,
  country_code: opts.country || 'PT',
  region: opts.region || 'eu-west',
  owner_id: ownerId,
  trophies: opts.trophies ?? 1,
  tournament_wins: opts.trophies ?? 1,
  is_active: true,
  deleted_at: null,
  recruitment_status: opts.recruitment || 'open',
  created_at: daysFromNow(-90),
  _memberIds: memberIds // internal helper used to build embedded relations
});

export const teams = [
  mkTeam('team-1', 'Vertex', 'user-me', ['user-me', 'user-1', 'user-2', 'user-3', 'user-4'], { country: 'PT', region: 'eu-west', trophies: 4 }),
  mkTeam('team-2', 'Nightfall', 'user-5', ['user-5', 'user-6', 'user-7'], { country: 'BR', region: 'sa', trophies: 2 }),
  mkTeam('team-3', 'Ironclad', 'user-3', ['user-3', 'user-8', 'user-1'], { country: 'DE', region: 'eu-east', trophies: 1 }),
  mkTeam('team-4', 'Solaris', 'user-2', ['user-2', 'user-4', 'user-6'], { country: 'FR', region: 'eu-west', trophies: 3 })
];

// Flat team_members table
export const team_members = teams.flatMap((t) =>
  t._memberIds.map((uid, i) => ({
    id: `tm-${t.id}-${uid}`,
    team_id: t.id,
    user_id: uid,
    role: uid === t.owner_id ? 'owner' : i === 1 ? 'captain' : 'player',
    joined_at: daysFromNow(-80 + i)
  }))
);

// Build the embedded team object as returned by nested selects
const embeddedTeam = (teamId) => {
  const t = teams.find((x) => x.id === teamId);
  if (!t) return null;
  return {
    id: t.id,
    name: t.name,
    logo: t.logo,
    description: t.description,
    country_code: t.country_code,
    owner_id: t.owner_id,
    tournament_wins: t.tournament_wins,
    trophies: t.trophies,
    owner: publicUser(t.owner_id),
    members: t._memberIds.map((uid) => ({
      user_id: uid,
      role: uid === t.owner_id ? 'owner' : 'player',
      user: publicUser(uid)
    }))
  };
};

// Attach embedded owner + members to the flat teams table rows, so queries that
// project `owner:users(...)` / `members:team_members(...)` find them (the mock
// returns whole rows and ignores the select projection).
teams.forEach((t) => {
  t.owner = publicUser(t.owner_id);
  t.members = t._memberIds.map((uid) => ({
    user: publicUser(uid),
    user_id: uid,
    role: uid === t.owner_id ? 'owner' : 'player',
    joined_at: daysFromNow(-80)
  }));
});

// ---------------------------------------------------------------------------
// tournaments (with embedded created_by + tournament_registrations)
// ---------------------------------------------------------------------------
const mkTournament = (id, name, format, status, opts = {}) => ({
  id,
  name,
  description: opts.description || `${name} — a Rematch Pro ${format} event.`,
  logo: opts.logo || null,
  start_date: isoDate(opts.startIn ?? 3),
  start_time: opts.startTime || '18:00',
  start_datetime: daysFromNow(opts.startIn ?? 3),
  scheduler_timezone: 'Europe/Lisbon',
  end_date: isoDate((opts.startIn ?? 3) + 7),
  max_teams: opts.maxTeams ?? 16,
  min_teams: opts.minTeams ?? 4,
  format,
  tournament_size_format: opts.size || '5v5',
  platform: opts.platform || 'all',
  discord_link: 'https://discord.gg/rematchpro',
  allowed_countries: opts.countries || null,
  allowed_regions: opts.regions || null,
  playoff_series_type: opts.playoff || 'single',
  knockout_series_type: opts.knockout || 'bo3',
  minutes_per_match: 10,
  golden_goal_overtime: true,
  invite_only: opts.inviteOnly || false,
  games_per_matchup: opts.gpm || 1,
  first_journey_date: daysFromNow(opts.startIn ?? 3),
  journey_duration: '1 day',
  total_journeys: opts.journeys ?? null,
  estimated_end_date: isoDate((opts.startIn ?? 3) + 14),
  status,
  prize_pool: opts.prize || '$500',
  rules: opts.rules || ['Be respectful.', 'No smurfing.', 'Golden goal in overtime.'],
  bracket_generated: !['open', 'draft', 'registration'].includes(status),
  bracket_generated_at: !['open', 'draft', 'registration'].includes(status) ? daysFromNow(-1) : null,
  knockout_bracket_generated: status === 'knockout' || status === 'completed',
  official_streams: opts.streams || [],
  require_schedule_approval: false,
  section_id: null,
  created_at: daysFromNow(opts.createdAgo ?? -10),
  created_by: publicUser('user-me'),
  tournament_registrations: (opts.teamIds || []).map((tid) => ({ team: embeddedTeam(tid) })),
  winner: opts.winnerTeamId
    ? (() => { const w = teams.find((t) => t.id === opts.winnerTeamId); return w ? { id: w.id, name: w.name, logo: w.logo } : null; })()
    : null
});

export const tournaments = [
  mkTournament('tour-1', 'Summer Open', 'single_elimination', 'open', {
    startIn: 5, maxTeams: 16, minTeams: 4, prize: '$1,000', createdAgo: -2,
    teamIds: ['team-1', 'team-2']
  }),
  mkTournament('tour-2', 'Champions Swiss', 'swiss', 'group_stage', {
    startIn: -1, maxTeams: 16, minTeams: 8, prize: '$2,500', createdAgo: -12,
    teamIds: ['team-1', 'team-2', 'team-3', 'team-4']
  }),
  mkTournament('tour-3', 'Pro League Season 1', 'league', 'open', {
    startIn: 7, maxTeams: 10, minTeams: 6, prize: '$5,000', createdAgo: -4,
    journeys: 9, teamIds: ['team-2', 'team-4']
  }),
  mkTournament('tour-4', 'ERCL Finals', 'ercl', 'knockout', {
    startIn: -3, maxTeams: 24, minTeams: 18, prize: '$10,000', createdAgo: -30,
    teamIds: ['team-1', 'team-3', 'team-4']
  }),
  mkTournament('tour-5', 'Winter Cup', 'single_elimination', 'completed', {
    startIn: -20, maxTeams: 8, minTeams: 4, prize: '$800', createdAgo: -40,
    winnerTeamId: 'team-1', teamIds: ['team-1', 'team-2', 'team-3']
  })
];

// ---------------------------------------------------------------------------
// tournament_registrations (flat table)
// ---------------------------------------------------------------------------
export const tournament_registrations = tournaments.flatMap((t) =>
  (t.tournament_registrations || []).map((reg, i) => ({
    id: `reg-${t.id}-${reg.team.id}`,
    tournament_id: t.id,
    team_id: reg.team.id,
    status: 'approved',
    registered_at: daysFromNow(-8 + i),
    // Embedded team (with members) for queries that project team:teams(...)
    team: embeddedTeam(reg.team.id)
  }))
);

// ---------------------------------------------------------------------------
// matches (with embedded teams) — enough to render brackets/lists
// ---------------------------------------------------------------------------
const mkMatch = (id, tournamentId, t1, t2, opts = {}) => ({
  id,
  tournament_id: tournamentId,
  series_id: opts.seriesId || id,
  round_number: opts.round ?? 1,
  match_number: opts.matchNumber ?? 1,
  team1_id: t1,
  team2_id: t2,
  team1_score: opts.s1 ?? null,
  team2_score: opts.s2 ?? null,
  winner_id: opts.winner || null,
  series_winner_id: opts.seriesWinner || null,
  status: opts.status || 'scheduled',
  scheduled_datetime: daysFromNow(opts.startIn ?? 1),
  team1: t1 ? { id: t1, name: teams.find((x) => x.id === t1)?.name, logo: null, members: (teams.find((x) => x.id === t1)?._memberIds || []).map((uid) => ({ user: publicUser(uid) })) } : null,
  team2: t2 ? { id: t2, name: teams.find((x) => x.id === t2)?.name, logo: null, members: (teams.find((x) => x.id === t2)?._memberIds || []).map((uid) => ({ user: publicUser(uid) })) } : null,
  winner: opts.winner ? { id: opts.winner, name: teams.find((x) => x.id === opts.winner)?.name, logo: null } : null,
  series_winner: null,
  player_stats: [],
  tournament_group: null,
  created_at: daysFromNow(-5)
});

export const matches = [
  mkMatch('match-1', 'tour-2', 'team-1', 'team-2', { round: 1, matchNumber: 1, status: 'completed', s1: 3, s2: 1, winner: 'team-1', startIn: -1 }),
  mkMatch('match-2', 'tour-2', 'team-3', 'team-4', { round: 1, matchNumber: 2, status: 'completed', s1: 2, s2: 2, startIn: -1 }),
  mkMatch('match-3', 'tour-2', 'team-1', 'team-3', { round: 2, matchNumber: 1, status: 'scheduled', startIn: 1 }),
  mkMatch('match-4', 'tour-4', 'team-1', 'team-4', { round: 101, matchNumber: 1, status: 'scheduled', startIn: 2 }),
  mkMatch('match-5', 'tour-5', 'team-1', 'team-2', { round: 1, matchNumber: 1, status: 'completed', s1: 4, s2: 0, winner: 'team-1', startIn: -20 })
];

// ---------------------------------------------------------------------------
// Misc small tables
// ---------------------------------------------------------------------------
export const partners = [
  { id: 'p-1', name: 'PixelForge', logo: null, url: 'https://example.com', description: 'Gear partner', tier: 'gold', active: true, created_at: daysFromNow(-60) },
  { id: 'p-2', name: 'BoostHub', logo: null, url: 'https://example.com', description: 'Hosting partner', tier: 'silver', active: true, created_at: daysFromNow(-50) }
];

export const site_settings = [
  { id: 'ss-1', key: 'coming_soon', value: 'false' },
  { id: 'ss-2', key: 'maintenance_mode', value: 'false' }
];

// ---------------------------------------------------------------------------
// Table registry consumed by the mock client. Each entry is a live array so
// inserts/updates/deletes during a session persist in memory.
// ---------------------------------------------------------------------------
export const tables = {
  users,
  teams,
  team_members,
  tournaments,
  tournament_registrations,
  tournament_owners: [{ tournament_id: 'tour-1', user_id: 'user-me' }],
  matches,
  partners,
  site_settings,
  chat_messages: [],
  player_match_stats: [],
  team_invitations: [],
  team_join_requests: [],
  // LFS (Looking For Scrim) scheduling tables — kept
  scrims: [],
  scrim_messages: [],
  scrim_requests: []
};

// Fake data for RPC calls, keyed by function name. Anything not listed returns [].
export const rpcResults = {
  get_tournament_winners: [
    { tournament_id: 'tour-5', winner_team_id: 'team-1', winner_team_name: 'Vertex' }
  ],
  get_tournaments_by_section: [],
  get_leaderboard: teams.map((t, i) => ({
    team_id: t.id, team_name: t.name, rank: i + 1, points: 30 - i * 4, wins: 10 - i, losses: 2 + i, mmr: 1000 + (teams.length - i) * 40
  })),
  get_global_mvp_leaderboard: users.slice(0, 6).map((u, i) => ({
    user_id: u.id, username: u.username, display_name: u.display_name, avatar_url: u.avatar_url, mvp_count: 8 - i, rank: i + 1
  })),
  get_players_paginated: users.map((u, i) => {
    const memberTeams = teams.filter((tm) => (tm._memberIds || []).includes(u.id));
    const wins = u.total_wins, matches = u.total_wins + u.total_losses;
    return {
      id: u.id, player_id: u.id, user_id: u.id, username: u.username, display_name: u.display_name, avatar_url: u.avatar_url,
      country_code: u.country_code, position: u.position, platform: u.platform, elo_rating: u.elo_rating, tournaments_won: u.tournaments_won,
      total_goals: Math.max(0, 30 - i * 3),
      total_assists: Math.max(0, 20 - i * 2),
      total_passes: Math.max(0, 400 - i * 30),
      total_interceptions: Math.max(0, 40 - i * 3),
      total_saves: Math.max(0, (u.position === 'Goalkeeper' ? 60 : 8) - i),
      total_mvps: Math.max(0, 8 - i),
      total_matches: matches,
      total_wins: wins,
      win_percentage: Math.round((wins / Math.max(1, matches)) * 100),
      current_team_name: memberTeams[0]?.name || null,
      additional_teams_count: Math.max(0, memberTeams.length - 1)
    };
  }),
  sync_discord_profile: { success: true, updated: false },
  get_active_tournament_sections: [],
  get_team_leaderboards: teams.map((t, i) => ({
    team_id: t.id,
    team_name: t.name,
    logo: t.logo,
    country_code: t.country_code,
    rank: i + 1,
    match_wins: 18 - i * 3,
    match_losses: 4 + i,
    win_rate: Math.round(((18 - i * 3) / Math.max(1, (18 - i * 3) + (4 + i))) * 100),
    tournaments_won: t.tournament_wins,
    elo_rating: 1000 + (teams.length - i) * 55,
    total_team_goals: 60 - i * 8,
    total_matches: 22 + i,
    total_wins: 18 - i * 3,
    owner: publicUser(t.owner_id)?.display_name || publicUser(t.owner_id)?.username || 'Owner',
    created_at: t.created_at
  })),
  get_player_leaderboards: users.map((u, i) => ({
    user_id: u.id,
    player_id: u.id,
    username: u.username,
    display_name: u.display_name,
    avatar_url: u.avatar_url,
    country_code: u.country_code,
    position: u.position,
    platform: u.platform,
    rank: i + 1,
    overall: 99 - i * 2,
    tournaments_won: u.tournaments_won,
    total_goals: 30 - i * 3,
    total_assists: 20 - i * 2,
    total_passes: 400 - i * 30,
    total_interceptions: 40 - i * 3,
    total_saves: (u.position === 'Goalkeeper' ? 60 : 5) - i,
    total_matches: u.total_wins + u.total_losses,
    total_wins: u.total_wins,
    win_rate: Math.round((u.total_wins / Math.max(1, u.total_wins + u.total_losses)) * 100),
    mvp_count: 8 - i,
    elo_rating: u.elo_rating
  }))
};
